import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { majorContractsService } from '@shared/services/majorContracts';
import { lengthOfServiceService } from '@modules/compensations/lengthOfService/services/lengthOfServiceService';
import { useForm } from 'react-hook-form';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';

const resolveTblClassOptionId = (rawValue, options = []) => {
    if (rawValue == null || rawValue === '') return '';
    const match = options.find(
        (opt) => String(opt.id) === String(rawValue) || String(opt.tblClassId) === String(rawValue)
    );
    return match ? String(match.id) : String(rawValue);
};

/** Convert API dates (dd.MM.yyyy or Date) to yyyy-MM-dd for <input type="date"> */
const formatDateForInput = (value) => {
    if (!value) return '';

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        return raw.slice(0, 10);
    }

    const bgMatch = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (bgMatch) {
        const [, day, month, year] = bgMatch;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    return '';
};

export default function WorkExperienceHeading({ person, onClose }) {
    const { t } = useTranslation();
    const { register, handleSubmit, setValue, watch, unregister, reset, formState: { errors } } = useForm({
        defaultValues: {
            catWork: 3,
        }
    });
    const [firstSectionLocked, setFirstSectionLocked] = useState(true);
    const [secondSectionLocked, setSecondSectionLocked] = useState(true);
    const [selectedContract, setSelectedContract] = useState('');
    const [mainContracts, setMainContracts] = useState([]);
    const [fromDate, setFromDate] = useState(null);

    // Get today's date in YYYY-MM-DD format for default value
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [toDate, setToDate] = useState(getTodayDate());
    // kind for correction section: 0 = Общ трудов стаж, 1 = Професионален стаж (other radios later)
    const [selectedKind, setSelectedKind] = useState('0');
    const [correctTableData, setCorrectTableData] = useState([]);
    // Correction form (fourth section: Корекция от)
    const [correctionFromDate, setCorrectionFromDate] = useState('');
    const [correctionYears, setCorrectionYears] = useState(0);
    const [correctionMonths, setCorrectionMonths] = useState(0);
    const [correctionDays, setCorrectionDays] = useState(0);
    // For kind 2/3: tbl form and table data
    // UI radio '3' (totalLengthOfServiceCorrection) maps to API kind 1
    const [tblTableData, setTblTableData] = useState([]);
    const [tblClassSelect, setTblClassSelect] = useState('');
    const [tblFromDate, setTblFromDate] = useState('');
    const [tblClassOptions, setTblClassOptions] = useState([]);
    // When set, we're editing this record (PUT); when null, we're creating (POST)
    const [editingRowId, setEditingRowId] = useState(null);
    const [hasWorkExperience, setHasWorkExperience] = useState(false);
    const watchedTblClassId = watch('tblClassId');

    const getTblApiKind = (kind = selectedKind) => (kind === '3' ? '1' : kind);

    useEffect(() => {
        const fetchTblClassOptions = async () => {
            try {
                const data = await lengthOfServiceService.getTblClassIdList();
                setTblClassOptions(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching tbl-class list:', error);
                setTblClassOptions([]);
            }
        };

        fetchTblClassOptions();
    }, []);

    // Function to calculate work experience between two dates
    const calculateWorkExperience = (startDate, endDate) => {
        if (!startDate || !endDate) {
            return { years: 0, months: 0, days: 0 };
        }

        const start = new Date(startDate);
        const end = new Date(endDate);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            return { years: 0, months: 0, days: 0 };
        }

        if (end < start) {
            return { years: 0, months: 0, days: 0 };
        }

        let years = end.getFullYear() - start.getFullYear();
        let months = end.getMonth() - start.getMonth();
        let days = end.getDate() - start.getDate();

        // Adjust for negative days
        if (days < 0) {
            months--;
            const lastDayOfPrevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
            days += lastDayOfPrevMonth.getDate();
        }

        // Adjust for negative months
        if (months < 0) {
            years--;
            months += 12;
        }

        return { years, months, days };
    };

    // Handler for "Преглед" button - calculate and populate work experience
    const handleCalculateExperience = () => {
        if (!fromDate || !toDate) {
            notifyError(t('common.errorMessage') || 'Please select both dates');
            return;
        }

        const experience = calculateWorkExperience(fromDate, toDate);

        // Find the second section container (third section in the component)
        // Look for the flex-row that contains "Трудов стаж към дата" text
        const secondSectionContainer = Array.from(document.querySelectorAll('.flex-row.width-100')).find(row => {
            const text = row.textContent || '';
            return text.includes('Трудов стаж към дата');
        });

        if (secondSectionContainer) {
            // Find all number inputs in the second column of this section
            const inputs = secondSectionContainer.querySelectorAll('.flex-column:nth-child(2) input[type="number"]');

            if (inputs.length >= 9) {
                // Years (first 3 inputs)
                inputs[0].value = experience.years;
                inputs[1].value = experience.years;
                inputs[2].value = experience.years;
                // Months (next 3 inputs)
                inputs[3].value = experience.months;
                inputs[4].value = experience.months;
                inputs[5].value = experience.months;
                // Days (last 3 inputs)
                inputs[6].value = experience.days;
                inputs[7].value = experience.days;
                inputs[8].value = experience.days;

                // Trigger change events to update React state if needed
                inputs.forEach(input => {
                    const event = new Event('input', { bubbles: true });
                    input.dispatchEvent(event);
                });

                console.log('✅ Values populated successfully');
            } else {
                console.log('❌ Could not find all 9 input fields. Found:', inputs.length);
            }
        } else {
            console.log('❌ Could not find second section container');
        }
    };

    // Submit handler for first section
    const onSubmitFirstSection = async (formData) => {
        try {
            // Get empId from person prop (could be person.empId or person.recordId)
            const empId = person?.empId

            if (!empId) {
                notifyError(t('common.errorMessage') || 'empId is missing');
                return;
            }

            // Validate contractId
            if (!selectedContract) {
                notifyError(t('common.errorMessage') || 'Please select a contract');
                console.log('selectedContract', selectedContract);
                return;
            }

            // Prepare data with only registered fields + empId + contractId
            const submitData = {
                ...formData,
                empId: empId,
                contractId: selectedContract,
                fromDate: fromDate || formData.fromDate
            };

            await majorContractsService.createWorkExperience(submitData);
            notifySuccess(t('common.updatedSuccessfully') || 'Updated successfully');
            setFirstSectionLocked(true);
            setHasWorkExperience(true);
        } catch (error) {
            console.error('Error submitting work experience:', error);
            const errorMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        }
    };

    useEffect(() => {
        const id = person?.recordId ?? person?.empId ?? person?.id;
        if (!id) return;
        const fetchMainContracts = async () => {
            const data = await majorContractsService.getMainContractsByPersonId(id);
            setMainContracts(data);
            if (data && data.length > 0) {
                // Only seed fromDate from contract when work-experience has not set it yet
                setFromDate((prev) => prev || formatDateForInput(data[0].effectiveDate));
                // Set default selected contract to first one
                if (!selectedContract && data[0].contractId) {
                    setSelectedContract(String(data[0].contractId));
                }
            }
        };
        fetchMainContracts();
    }, [person?.recordId, person?.empId, person?.id]);

    // Fetch existing work experience data when component mounts
    useEffect(() => {
        const fetchWorkExperience = async () => {
            try {
                // Get empId from person prop
                const empId = person?.empId

                if (!empId) {
                    setHasWorkExperience(false);
                    return; // Can't fetch without empId
                }

                const workExperienceData = await majorContractsService.getWorkExperience(empId);

                // Handle both array and single object responses
                const data = Array.isArray(workExperienceData)
                    ? (workExperienceData.length > 0 ? workExperienceData[0] : null)
                    : workExperienceData;

                setHasWorkExperience(!!data);

                // If data exists, populate the form
                if (data) {
                    console.log('Fetched work experience data:', data);

                    // Populate all registered form fields
                    if (data.fromDate) {
                        const normalizedFromDate = formatDateForInput(data.fromDate);
                        setValue('fromDate', normalizedFromDate, { shouldValidate: false });
                        setFromDate(normalizedFromDate);
                    }
                    if (data.kind0Years !== undefined && data.kind0Years !== null) setValue('kind0Years', data.kind0Years, { shouldValidate: false });
                    if (data.kind1Years !== undefined && data.kind1Years !== null) setValue('kind1Years', data.kind1Years, { shouldValidate: false });
                    if (data.kind2Years !== undefined && data.kind2Years !== null) setValue('kind2Years', data.kind2Years, { shouldValidate: false });
                    if (data.kind0Months !== undefined && data.kind0Months !== null) setValue('kind0Months', data.kind0Months, { shouldValidate: false });
                    // Map kind1Months from response to kind1Months in form (form uses kind3, response has kind1)
                    if (data.kind1Months !== undefined && data.kind1Months !== null) setValue('kind1Months', data.kind1Months, { shouldValidate: false });
                    if (data.kind2Months !== undefined && data.kind2Months !== null) setValue('kind2Months', data.kind2Months, { shouldValidate: false });
                    if (data.kind0Days !== undefined && data.kind0Days !== null) setValue('kind0Days', data.kind0Days, { shouldValidate: false });
                    // Map kind1Days from response to kind1Days in form (form uses kind3, response has kind1)
                    if (data.kind1Days !== undefined && data.kind1Days !== null) setValue('kind1Days', data.kind1Days, { shouldValidate: false });
                    if (data.kind2Days !== undefined && data.kind2Days !== null) setValue('kind2Days', data.kind2Days, { shouldValidate: false });
                    if (data.catWork !== undefined && data.catWork !== null) setValue('catWork', data.catWork, { shouldValidate: false });
                    if (data.tblClassId !== undefined && data.tblClassId !== null) {
                        setValue('tblClassId', resolveTblClassOptionId(data.tblClassId, tblClassOptions), { shouldValidate: false });
                    }

                    // Set contractId if it exists
                    if (data.contractId) {
                        setSelectedContract(String(data.contractId));
                    }
                }
            } catch (error) {
                console.error('Error fetching work experience:', error);
                setHasWorkExperience(false);
                // Don't show error notification on initial load - data might not exist yet
            }
        };

        fetchWorkExperience();
    }, [person?.recordId, person?.empId, person?.id, setValue, tblClassOptions]);

    // Re-map selected tblClass values once options are loaded
    useEffect(() => {
        if (!tblClassOptions.length) return;

        if (watchedTblClassId) {
            const resolved = resolveTblClassOptionId(watchedTblClassId, tblClassOptions);
            if (resolved !== String(watchedTblClassId)) {
                setValue('tblClassId', resolved, { shouldValidate: false });
            }
        }

        if (tblClassSelect) {
            const resolved = resolveTblClassOptionId(tblClassSelect, tblClassOptions);
            if (resolved !== String(tblClassSelect)) {
                setTblClassSelect(resolved);
            }
        }
    }, [tblClassOptions, watchedTblClassId, tblClassSelect, setValue]);

    // Refetch getWorkExperience and show in first section (call after any successful CRUD in last section)
    const refetchWorkExperience = useCallback(async () => {
        const empId = person?.empId || person?.recordId || person?.id;
        if (!empId) return;
        try {
            const workExperienceData = await majorContractsService.getWorkExperience(empId);
            const data = Array.isArray(workExperienceData)
                ? (workExperienceData.length > 0 ? workExperienceData[0] : null)
                : workExperienceData;
            setHasWorkExperience(!!data);
            if (data) {
                if (data.fromDate) {
                    const normalizedFromDate = formatDateForInput(data.fromDate);
                    setValue('fromDate', normalizedFromDate, { shouldValidate: false });
                    setFromDate(normalizedFromDate);
                }
                if (data.kind0Years !== undefined && data.kind0Years !== null) setValue('kind0Years', data.kind0Years, { shouldValidate: false });
                if (data.kind1Years !== undefined && data.kind1Years !== null) setValue('kind1Years', data.kind1Years, { shouldValidate: false });
                if (data.kind2Years !== undefined && data.kind2Years !== null) setValue('kind2Years', data.kind2Years, { shouldValidate: false });
                if (data.kind0Months !== undefined && data.kind0Months !== null) setValue('kind0Months', data.kind0Months, { shouldValidate: false });
                if (data.kind1Months !== undefined && data.kind1Months !== null) setValue('kind1Months', data.kind1Months, { shouldValidate: false });
                if (data.kind2Months !== undefined && data.kind2Months !== null) setValue('kind2Months', data.kind2Months, { shouldValidate: false });
                if (data.kind0Days !== undefined && data.kind0Days !== null) setValue('kind0Days', data.kind0Days, { shouldValidate: false });
                if (data.kind1Days !== undefined && data.kind1Days !== null) setValue('kind1Days', data.kind1Days, { shouldValidate: false });
                if (data.kind2Days !== undefined && data.kind2Days !== null) setValue('kind2Days', data.kind2Days, { shouldValidate: false });
                if (data.catWork !== undefined && data.catWork !== null) setValue('catWork', data.catWork, { shouldValidate: false });
                if (data.tblClassId !== undefined && data.tblClassId !== null) {
                    setValue('tblClassId', resolveTblClassOptionId(data.tblClassId, tblClassOptions), { shouldValidate: false });
                }
                if (data.contractId) setSelectedContract(String(data.contractId));
            }
        } catch (e) {
            console.error('Error refetching work experience:', e);
        }
    }, [person?.empId, person?.recordId, person?.id, setValue, tblClassOptions]);

    // Fetch table data when empId or selectedKind changes
    useEffect(() => {
        const id = person?.empId
        if (!id) return;
        if (selectedKind === '0' || selectedKind === '1') {
            const fetchCorrectTable = async () => {
                try {
                    const data = await majorContractsService.getLengthServiceCorrect(id, selectedKind);
                    setCorrectTableData(Array.isArray(data) ? data : (data ? [data] : []));
                } catch (error) {
                    console.error('Error fetching length service correct:', error);
                    setCorrectTableData([]);
                }
            };
            fetchCorrectTable();
            setTblTableData([]);
        } else if (selectedKind === '2' || selectedKind === '3') {
            const fetchTblTable = async () => {
                try {
                    const data = await majorContractsService.getLengthServiceTbl(id, getTblApiKind(selectedKind));
                    setTblTableData(Array.isArray(data) ? data : (data ? [data] : []));
                } catch (error) {
                    console.error('Error fetching length service tbl:', error);
                    setTblTableData([]);
                }
            };
            fetchTblTable();
            setCorrectTableData([]);
        }
    }, [person?.recordId, person?.empId, person?.id, selectedKind]);

    const selectedContractData = mainContracts.find(c => String(c.contractId) === String(selectedContract));

    const onSubmitCorrect = async () => {
        try {
            const id = person?.empId;
            if (!id) {
                notifyError(t('common.errorMessage') || 'empId is missing');
                return;
            }
            if (!selectedContract) {
                notifyError(t('common.errorMessage') || 'Please select a contract');
                return;
            }
            if (editingRowId != null) {
                const body = {
                    fromDate: correctionFromDate || fromDate,
                    years: Number(correctionYears) || 0,
                    months: Number(correctionMonths) || 0,
                    days: Number(correctionDays) || 0,
                };
                await majorContractsService.updateLengthServiceCorrect(editingRowId, body);
            } else {
                const tblClassId = watch('tblClassId');
                const body = {
                    empId: String(id),
                    contractId: Number(selectedContract),
                    fromDate: correctionFromDate || fromDate,
                    tblClassId: tblClassId != null && tblClassId !== '' ? Number(tblClassId) : null,
                    kind: String(selectedKind),
                    years: Number(correctionYears) || 0,
                    months: Number(correctionMonths) || 0,
                    days: Number(correctionDays) || 0,
                };
                await majorContractsService.createLengthServiceCorrect(body);
            }
            notifySuccess(t('common.updatedSuccessfully') || 'Updated successfully');
            setSecondSectionLocked(true);
            setEditingRowId(null);
            const data = await majorContractsService.getLengthServiceCorrect(id, selectedKind);
            setCorrectTableData(Array.isArray(data) ? data : (data ? [data] : []));
            await refetchWorkExperience();
        } catch (error) {
            console.error('Error submitting correction:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        }
    };

    const onSubmitTbl = async () => {
        const empId = person?.empId;
        try {
            if (!empId) {
                notifyError(t('common.errorMessage') || 'empId is missing');
                return;
            }
            if (!selectedContract) {
                notifyError(t('common.errorMessage') || 'Please select a contract');
                return;
            }
            if (editingRowId != null) {
                const body = {
                    fromDate: tblFromDate,
                    tblClassId: tblClassSelect ? Number(tblClassSelect) : null,
                };
                await majorContractsService.updateLengthServiceTbl(editingRowId, body);
            } else {
                const body = {
                    empId: String(empId),
                    contractId: Number(selectedContract),
                    fromDate: tblFromDate,
                    tblClassId: tblClassSelect ? Number(tblClassSelect) : null,
                    kind: Number(getTblApiKind(selectedKind)),
                };
                await majorContractsService.createLengthServiceTbl(body);
            }
            notifySuccess(t('common.updatedSuccessfully') || 'Updated successfully');
            setSecondSectionLocked(true);
            setEditingRowId(null);
            const id = person?.empId ?? person?.id;
            if (id) {
                const data = await majorContractsService.getLengthServiceTbl(id, getTblApiKind(selectedKind));
                setTblTableData(Array.isArray(data) ? data : (data ? [data] : []));
            }
            await refetchWorkExperience();
        } catch (error) {
            console.error('Error submitting length service tbl:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        }
    };

    // Edit: fill form with row data and unlock section (PUT on Запис)
    const handleEditCorrect = (row) => {
        if (row?.id == null) return;
        setEditingRowId(row.id);
        setCorrectionFromDate(row.fromDate ?? '');
        setCorrectionYears(Number(row.years) ?? 0);
        setCorrectionMonths(Number(row.months) ?? 0);
        setCorrectionDays(Number(row.days) ?? 0);
        setSecondSectionLocked(false);
    };
    const handleEditTbl = (row) => {
        if (row?.id == null) return;
        setEditingRowId(row.id);
        setTblFromDate(row.fromDate ?? '');
        setTblClassSelect(resolveTblClassOptionId(row.tblClassId, tblClassOptions));
        setSecondSectionLocked(false);
    };

    const handleDeleteRow = async (row) => {
        if (row?.id == null) return;
        const message = 'Внимание! Изтриването на записа може да доведе до некоректни резултати! Потвърдете изтриването!?';
        if (!window.confirm(message)) return;
        const tblKind = selectedKind === '2' || selectedKind === '3';
        try {
            const id = person?.empId ?? person?.recordId ?? person?.id;
            if (tblKind) {
                await majorContractsService.deleteLengthServiceTbl(row.id);
                const data = await majorContractsService.getLengthServiceTbl(id, getTblApiKind(selectedKind));
                setTblTableData(Array.isArray(data) ? data : (data ? [data] : []));
            } else {
                await majorContractsService.deleteLengthServiceCorrect(row.id);
                const data = await majorContractsService.getLengthServiceCorrect(id, selectedKind);
                setCorrectTableData(Array.isArray(data) ? data : (data ? [data] : []));
            }
            setEditingRowId(null);
            notifySuccess(t('common.updatedSuccessfully') || 'Изтрито успешно');
            await refetchWorkExperience();
        } catch (error) {
            console.error('Error deleting record:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        }
    };

    const isCorrectKind = selectedKind === '0' || selectedKind === '1';
    const isTblKind = selectedKind === '2' || selectedKind === '3';

    return (
        <>
            <h3 className="form-subtitle">
                {t('persons.workExperience.title', {
                    name: [person.name, person.fathersName, person.surname]
                        .filter(Boolean)
                        .join(' ')
                })}
            </h3>
            {/* Big container */}
            <div className="flex-column width-100 position-center gap-10 mt-10 form-border-radius">
                {/* First section */}
                <div className="flex-column width-50 border-radius-10 position-relative">
                    <label className="text-left label-absolute">{t('persons.workExperience.employmentContracts')}</label>
                    <select
                        className="small-select"
                        value={selectedContract}
                        onChange={(e) => setSelectedContract(e.target.value)}
                    >
                        {mainContracts.map(contract => (
                            <option key={contract.contractId} value={contract.contractId}>
                                {contract.name} - {contract.effectiveDate}
                            </option>
                        ))}
                    </select>
                </div>
                {/* Second section */}
                <div className="flex-column gap-10">
                    <div className="flex-row width-100 justify-center section-divider">
                        {/* First column of second container */}
                        <div className="flex-column justify-space-between">
                            <div className="flex-row">
                                <span className="no-wrap">{t('persons.workExperience.inForceFrom')}</span>
                            </div>
                            <div className="flex-column">
                                <span className="no-wrap">{t('persons.workExperience.totalLengthOfService')}</span>
                                <span className="no-wrap">{t('persons.workExperience.lengthOfServiceInTheCompany')}</span>
                                <span className="no-wrap">{t('persons.workExperience.professionalExperience')}</span>
                            </div>

                        </div>
                        {/* Second column of second container */}
                        <div className="flex-column">
                            <input
                                type="date"
                                value={fromDate || ''}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="small-input width-50"
                                disabled={true}
                                {...register('fromDate')}
                            />
                            <div className="flex-row">
                                <div className="flex-column">
                                    <span className="no-wrap text-align-center">{t('persons.workExperience.years')}</span>
                                    <input type="number" className="small-input width-80px" disabled={firstSectionLocked} {...register('kind0Years')} />
                                    <input type="number" className="small-input width-80px" disabled={firstSectionLocked} {...register('kind2Years')} />
                                    <input type="number" className="small-input width-80px" disabled={firstSectionLocked} {...register('kind1Years')} />
                                </div>
                                <div className="flex-column">
                                    <span className="no-wrap text-align-center">{t('persons.workExperience.months')}</span>
                                    <input type="number" className="small-input width-80px" disabled={firstSectionLocked} {...register('kind0Months')} />
                                    <input type="number" className="small-input width-80px" disabled={firstSectionLocked} {...register('kind2Months')} />
                                    <input type="number" className="small-input width-80px" disabled={firstSectionLocked} {...register('kind1Months')} />
                                </div>
                                <div className="flex-column">
                                    <span className="no-wrap text-align-center">{t('persons.workExperience.days')}</span>
                                    <input type="number" className="small-input width-80px" disabled={firstSectionLocked} {...register('kind0Days')} />
                                    <input type="number" className="small-input width-80px" disabled={firstSectionLocked} {...register('kind2Days')} />
                                    <input type="number" className="small-input width-80px" disabled={firstSectionLocked} {...register('kind1Days')} />
                                </div>
                            </div>
                        </div>
                        {/* Third column of second container */}
                        <div className="flex-column justify-space-between">
                            <div className="flex-row">
                                <span className="no-wrap">{t('persons.workExperience.workCategory')}</span>
                                <input type="number" className="small-input width-80px" disabled={firstSectionLocked} {...register('catWork')} />
                            </div>
                            <div className="flex-row">
                                <div className="flex-column">
                                    <span className="no-wrap">{t('persons.workExperience.tableNumberForProfessionalLengthOfService')}</span>
                                    <span className="no-wrap"> {t('persons.workExperience.totalLengthOfServiceCorrection')} </span>
                                </div>
                                <div className="flex-column">
                                    <select className="small-select" disabled={firstSectionLocked}>
                                        <option value="1">1</option>
                                    </select>
                                    <select className="small-select" disabled={firstSectionLocked} {...register('tblClassId')} >
                                        <option value="">{t('common.select')}</option>
                                        {tblClassOptions.map((opt) => (
                                            <option key={opt.id} value={opt.id}>{opt.tblClassId}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                        </div>
                        {/* Fourth column of second container */}
                        {firstSectionLocked ? (
                            <div className="flex-column justify-flex-end">
                                <button className={`form-btn btn-blue height-80 ${hasWorkExperience ? 'btn-disabled' : ''}`} onClick={() => setFirstSectionLocked(false)} disabled={hasWorkExperience}>{t('persons.workExperience.input')}</button>
                            </div>
                        ) : (
                            <div className="flex-column justify-flex-end gap-10">
                                <button
                                    type="button"
                                    className="form-btn btn-blue height-80"
                                    onClick={handleSubmit(onSubmitFirstSection)}
                                >
                                    {t('common.save')}
                                </button>
                                <button
                                    type="button"
                                    className="form-btn btn-red height-80"
                                    onClick={() => {
                                        reset(); // Reset form values
                                        setFirstSectionLocked(true);
                                    }}
                                >
                                    {t('common.cancel')}
                                </button>
                            </div>
                        )}

                    </div>
                    {/* Third section*/}
                    <div className="flex-row width-100 section-divider">
                        {/* First column of second container */}
                        <div className="flex-column justify-space-between">
                            <div className="flex-row">
                                <span className="no-wrap">{t('persons.workExperience.toDate')}</span>
                            </div>
                            <div className="flex-column">
                                <span className="no-wrap">{t('persons.workExperience.totalLengthOfService')}</span>
                                <span className="no-wrap">{t('persons.workExperience.lengthOfServiceInTheCompany')}</span>
                                <span className="no-wrap">{t('persons.workExperience.professionalExperience')}</span>
                            </div>

                        </div>
                        {/* Second column of second container */}
                        <div className="flex-column">
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="small-input width-50"
                            />
                            <div className="flex-row">
                                <div className="flex-column">
                                    <span className="no-wrap text-align-center">{t('persons.workExperience.years')}</span>
                                    <input type="number" className="small-input width-80px" disabled={secondSectionLocked} />
                                    <input type="number" className="small-input width-80px" disabled={secondSectionLocked} />
                                    <input type="number" className="small-input width-80px" disabled={secondSectionLocked} />
                                </div>
                                <div className="flex-column">
                                    <span className="no-wrap text-align-center">{t('persons.workExperience.months')}</span>
                                    <input type="number" className="small-input width-80px" disabled={secondSectionLocked} />
                                    <input type="number" className="small-input width-80px" disabled={secondSectionLocked} />
                                    <input type="number" className="small-input width-80px" disabled={secondSectionLocked} />
                                </div>
                                <div className="flex-column">
                                    <span className="no-wrap text-align-center">{t('persons.workExperience.days')}</span>
                                    <input type="number" className="small-input width-80px" disabled={secondSectionLocked} />
                                    <input type="number" className="small-input width-80px" disabled={secondSectionLocked} />
                                    <input type="number" className="small-input width-80px" disabled={secondSectionLocked} />
                                </div>
                            </div>
                        </div>
                        {/* Third column of second container */}
                        <div className="flex-column justify-space-between">
                            <div className="flex-row">
                                <button
                                    type="button"
                                    className="form-btn btn-blue"
                                    onClick={handleCalculateExperience}
                                    disabled={!fromDate || !toDate}
                                >
                                    {t('persons.workExperience.printPreview')}
                                </button>
                            </div>
                            <div className="flex-row">
                                <div className="flex-column">
                                    <span className="no-wrap">{t('persons.workExperience.tableNumberForProfessionalLengthOfService')}</span>
                                    <span className="no-wrap">{t('persons.workExperience.totalLengthOfServiceCorrection')}</span>
                                </div>
                                <div className="flex-column">
                                    <select className="small-select" disabled={true}>
                                        <option value="1">1</option>
                                    </select>
                                    <select className="small-select" disabled={true} value={watchedTblClassId || ''}>
                                        <option value="">{t('common.select')}</option>
                                        {tblClassOptions.map((opt) => (
                                            <option key={`mirror-${opt.id}`} value={opt.id}>{opt.tblClassId}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                        </div>
                    </div>
                    {/* Fourth section*/}
                    <div className="flex-column">
                        <h3 className="form-subtitle">
                            {selectedContractData ? `${selectedContractData.name} - ${selectedContractData.effectiveDate}` : ''}
                        </h3>
                    </div>
                    <div className="flex-row width-100">
                        {/* First column of fourth container */}
                        <div className="flex-column">
                            <div className="flex-column">
                                <span className="no-wrap">{t('persons.workExperience.selectionForChange')}</span>
                            </div>
                            <div className="flex-column">

                                <div className="flex-row">
                                    <input
                                        type="radio"
                                        name="totalWorkExperience"
                                        value="0"
                                        checked={selectedKind === '0'}
                                        onChange={() => setSelectedKind('0')}
                                    />
                                    <span className="no-wrap">{t('persons.workExperience.totalLengthOfService')}</span>
                                </div>
                                <div className="flex-row">
                                    <input
                                        type="radio"
                                        name="totalWorkExperience"
                                        value="1"
                                        checked={selectedKind === '1'}
                                        onChange={() => setSelectedKind('1')}
                                    />
                                    <span className="no-wrap">{t('persons.workExperience.professionalExperience')}</span>
                                </div>
                                <div className="flex-row">
                                    <input
                                        type="radio"
                                        name="totalWorkExperience"
                                        value="2"
                                        checked={selectedKind === '2'}
                                        onChange={() => setSelectedKind('2')}
                                    />
                                    <span className="no-wrap">{t('persons.workExperience.tableNumberForProfessionalLengthOfService')}</span>
                                </div>
                                <div className="flex-row">
                                    <input
                                        type="radio"
                                        name="totalWorkExperience"
                                        value="3"
                                        checked={selectedKind === '3'}
                                        onChange={() => setSelectedKind('3')}
                                    />
                                    <span className="no-wrap">{t('persons.workExperience.totalLengthOfServiceCorrection')}</span>
                                </div>
                            </div>
                        </div>
                        {/* Second column of fourth container */}
                        <div className="flex-column justify-space-between row-section-divider-left">
                            {isCorrectKind && (
                                <>
                                    <span className="no-wrap">{t('persons.workExperience.totalLengthOfService')}:</span>
                                    <div className="flex-row align-end">
                                        <span className="no-wrap">{t('persons.workExperience.correction')}</span>
                                        <input
                                            type="date"
                                            className="small-input"
                                            value={correctionFromDate}
                                            onChange={(e) => setCorrectionFromDate(e.target.value)}
                                            disabled={secondSectionLocked}
                                            min={fromDate || undefined}
                                        />
                                        <div className="flex-column">
                                            <span className="no-wrap">{t('persons.workExperience.years')}</span>
                                            <input
                                                type="number"
                                                className="small-input width-80px"
                                                value={correctionYears}
                                                onChange={(e) => setCorrectionYears(Number(e.target.value) || 0)}
                                                disabled={secondSectionLocked}
                                            />
                                        </div>
                                        <div className="flex-column">
                                            <span className="no-wrap">{t('persons.workExperience.months')}</span>
                                            <input
                                                type="number"
                                                className="small-input width-80px"
                                                value={correctionMonths}
                                                onChange={(e) => setCorrectionMonths(Number(e.target.value) || 0)}
                                                disabled={secondSectionLocked}
                                            />
                                        </div>
                                        <div className="flex-column">
                                            <span className="no-wrap">{t('persons.workExperience.days')}</span>
                                            <input
                                                type="number"
                                                className="small-input width-80px"
                                                value={correctionDays}
                                                onChange={(e) => setCorrectionDays(Number(e.target.value) || 0)}
                                                disabled={secondSectionLocked}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {isTblKind && (
                                <>
                                    <span className="no-wrap">
                                        {selectedKind === '2' ? `${t('persons.workExperience.tableNumberForProfessionalLengthOfService')}:` : `${t('persons.workExperience.totalLengthOfServiceCorrection')}:`}
                                    </span>
                                    <div className="flex-row">
                                        <div className="flex-column gap-10">
                                            <span className="no-wrap">{t('persons.workExperience.smallTotalLengthOfService')}</span>
                                            <span className="no-wrap">{t('persons.workExperience.inForceFrom')}:</span>
                                        </div>
                                        <div className="flex-column gap-10">
                                            <select
                                                className="small-select"
                                                value={tblClassSelect}
                                                onChange={(e) => setTblClassSelect(e.target.value)}
                                                disabled={secondSectionLocked}
                                            >
                                                <option value="">{t('common.select') || 'Избери'}</option>
                                                {tblClassOptions.map((opt) => (
                                                    <option key={`tbl-${opt.id}`} value={opt.id}>{opt.tblClassId}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="date"
                                                className="small-input"
                                                value={tblFromDate}
                                                onChange={(e) => setTblFromDate(e.target.value)}
                                                disabled={secondSectionLocked}
                                            />
                                        </div>
                                    </div>
                                </>

                            )}
                        </div>
                        {/* Third column of fourth container */}
                        {secondSectionLocked ? (
                            <div className="flex-column justify-flex-end">
                                <button className="form-btn btn-blue height-80" onClick={() => { setEditingRowId(null); setSecondSectionLocked(false); }}>{t('persons.workExperience.input')}</button>
                            </div>
                        ) : (
                            <div className="flex-column justify-flex-end gap-10">
                                <button type="button" className="form-btn btn-blue height-80" onClick={isCorrectKind ? onSubmitCorrect : onSubmitTbl}>{t('common.save')}</button>
                                <button type="button" className="form-btn btn-red height-80" onClick={() => { setEditingRowId(null); setSecondSectionLocked(true); }}>{t('common.cancel')}</button>
                            </div>
                        )}
                    </div>
                    <table className="table">
                        <thead>
                            <tr>
                                <th></th>
                                <th></th>
                                {isTblKind ? (
                                    <>
                                        <th>{t('persons.workExperience.calculatedLengthOfService')}</th>
                                        <th>{t('persons.workExperience.inForceFrom')}</th>
                                    </>
                                ) : (
                                    <>
                                        <th>{t('persons.workExperience.fromDate')}</th>
                                        <th>{t('persons.workExperience.years')}</th>
                                        <th>{t('persons.workExperience.months')}</th>
                                        <th>{t('persons.workExperience.days')}</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                const tableData = isTblKind ? tblTableData : correctTableData;
                                if (tableData.length === 0) {
                                    return (
                                        <tr>
                                            <td colSpan={isTblKind ? 4 : 6} className="text-align-center">{t('common.noData')}</td>
                                        </tr>
                                    );
                                }
                                return tableData.map((row, index) => (
                                    <tr key={row.id ?? index}>
                                        <td>
                                            <img
                                                src='/images/icons/edit-mini.png'
                                                alt='edit'
                                                title="Edit"
                                                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    isTblKind ? handleEditTbl(row) : handleEditCorrect(row);
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <img
                                                src='/images/icons/delete.png'
                                                alt='delete'
                                                title="Delete"
                                                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteRow(row);
                                                }}
                                            />
                                        </td>
                                        {isTblKind ? (
                                            <>
                                                <td>{row.tblClassId ?? row.tblClass ?? '–'}</td>
                                                <td>{row.fromDate ?? ''}</td>
                                            </>
                                        ) : (
                                            <>
                                                <td>{row.fromDateFormatted ?? ''}</td>
                                                <td>{row.years ?? ''}</td>
                                                <td>{row.months ?? ''}</td>
                                                <td>{row.days ?? ''}</td>
                                            </>
                                        )}
                                    </tr>
                                ));
                            })()}
                        </tbody>
                    </table>
                </div>
            </div >
            <div className="form-controls">
                <button className="form-btn btn-red" onClick={onClose}>{t('common.exit')}</button>
            </div>
        </>
    );
}
